import React, { useState, useRef, useEffect } from 'react';
import { useLoaderData } from 'react-router-dom';
import { Star, MapPin, Heart, Share2, ChevronRight, Award, Shield, Clock as _Clock, Users as _Users, Mountain, Calendar as CalendarIcon } from 'lucide-react';
import Header from '../../components/layout/Header';
import Breadcrumb from '../../components/layout/Breadcrumb';
import Footer from '../../components/layout/Footer';
import UrgencyIndicators from '../../components/shared/UrgencyIndicators';
import ExperienceCard from '../../components/experiences/ExperienceCard';
import ReviewsSection from '../../components/shared/ReviewsSection';
import { useTheme } from '../../hooks/useTheme';
import { getGuidePhotoUrl, getGuidePhotoSrcSet } from '../../utils/guideDataAdapters';
import { getResponsiveSizes } from '../../services/assetService';

/**
 * GuideProfilePage Component
 * Public guide profile page — data fetched via route loader
 */
export default function GuideProfilePage({ onAuthClick }) {
  const theme = useTheme();
  const { profile, error } = useLoaderData();
  const [selectedLocationFilter, setSelectedLocationFilter] = useState(null);
  const [selectedActivityFilter, setSelectedActivityFilter] = useState(null);
  const [selectedSeasonFilter, setSelectedSeasonFilter] = useState(null);
  const tripsRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header onAuthClick={onAuthClick} showSearch={true} />
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <Mountain className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Profile Not Available</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <a
            href="/"
            className={`${theme.buttonPrimary} inline-block px-6 py-3 rounded-lg font-semibold transition`}
          >
            Back to Home
          </a>
        </div>
        <Footer />
      </div>
    );
  }

  // Empty / not found state
  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header onAuthClick={onAuthClick} showSearch={true} />
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <Mountain className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Guide Not Found</h1>
          <p className="text-gray-600 mb-6">This guide profile does not exist or has not been approved yet.</p>
          <a
            href="/"
            className={`${theme.buttonPrimary} inline-block px-6 py-3 rounded-lg font-semibold transition`}
          >
            Back to Home
          </a>
        </div>
        <Footer />
      </div>
    );
  }

  // Map API response to display-friendly guide object
  const guide = {
    id: profile.guide_id,
    name: `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Guide',
    firstName: profile.first_name || 'Guide',
    profile_photo_url: profile.profile_photo_url || null,
    cover_photo_url: profile.cover_photo_url || null,
    // Pass through StoredAsset objects if enriched by SCRUM-787
    profilePhoto: profile.profilePhoto || null,
    coverPhoto: profile.coverPhoto || null,
    location: profile.business_name || '',
    avgRating: profile.avg_rating ? parseFloat(profile.avg_rating) : 0,
    totalReviews: profile.total_reviews || 0,
    totalTrips: profile.total_trips || 0,
    bio: profile.bio || '',
    serviceDescription: profile.service_description || '',
    experienceRange: profile.experience_range || '',
    primaryActivity: profile.primary_activity || '',
    certifications: profile.certifications || [],
  };

  // Urgency indicators from social proof cache
  const urgencyData = [];
  if (profile.current_viewers && profile.current_viewers > 0) {
    urgencyData.push({
      type: 'viewing',
      highlight: `${profile.current_viewers} ${profile.current_viewers === 1 ? 'person' : 'people'}`,
      text: `viewing ${guide.firstName}'s trips right now`,
    });
  }
  if (profile.bookings_last_30_days && profile.bookings_last_30_days > 0) {
    urgencyData.push({
      type: 'availability',
      highlight: `${profile.bookings_last_30_days} trips`,
      text: 'booked in the last 30 days',
    });
  }
  if (profile.has_limited_availability) {
    urgencyData.push({
      type: 'bookings',
      highlight: 'Limited availability',
      text: 'Book now to secure your preferred dates',
    });
  }

  // TODO: Fetch guide experiences from a separate API endpoint
  const allServices = [];

  const breadcrumbItems = [
    { label: 'Home', to: '/' },
    { label: 'Guides', to: '/' },
    { label: guide.name }
  ];

  // Filter logic (for when experiences are loaded)
  const locations = ['All Locations', ...new Set(allServices.map(s => s.location))];
  const activities = ['All Activities', ...new Set(allServices.map(s => s.activity))];
  const seasons = ['All Seasons', ...new Set(allServices.map(s => s.season))];

  const filteredServices = allServices.filter(service => {
    if (selectedLocationFilter && service.location !== selectedLocationFilter) {return false;}
    if (selectedActivityFilter && service.activity !== selectedActivityFilter) {return false;}
    if (selectedSeasonFilter && service.season !== selectedSeasonFilter) {return false;}
    return true;
  });

  const hasActiveFilters = selectedLocationFilter || selectedActivityFilter || selectedSeasonFilter;

  const clearFilters = () => {
    setSelectedLocationFilter(null);
    setSelectedActivityFilter(null);
    setSelectedSeasonFilter(null);
  };

  const scrollToTrips = () => {
    tripsRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onAuthClick={onAuthClick} showSearch={true} />
      <Breadcrumb items={breadcrumbItems} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Cover Photo with Profile Overlay */}
        <div className="relative mb-6 rounded-xl overflow-hidden shadow-lg">
          {guide.cover_photo_url || guide.coverPhoto ? (
            <img
              src={getGuidePhotoUrl(guide, 'cover')}
              srcSet={getGuidePhotoSrcSet(guide, 'cover') || undefined}
              sizes={getGuidePhotoSrcSet(guide, 'cover') ? getResponsiveSizes('hero') : undefined}
              alt="Guide cover"
              className="w-full h-64 md:h-80 object-cover"
            />
          ) : (
            <div className="w-full h-64 md:h-80 bg-gradient-to-r from-emerald-600 to-teal-700" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

          {/* Share and Favorite Icons - Top Right */}
          <div className="absolute top-6 right-6 flex gap-2">
            <button className="bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition">
              <Share2 className="w-5 h-5 text-gray-700" />
            </button>
            <button className="bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition">
              <Heart className="w-5 h-5 text-gray-700" />
            </button>
          </div>

          {/* Profile Photo Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <div className="flex items-end gap-6">
              {guide.profile_photo_url || guide.profilePhoto ? (
                <img
                  src={getGuidePhotoUrl(guide, 'profile')}
                  srcSet={getGuidePhotoSrcSet(guide, 'profile') || undefined}
                  sizes={getGuidePhotoSrcSet(guide, 'profile') ? getResponsiveSizes('profile') : undefined}
                  alt={guide.name}
                  className="w-32 h-32 rounded-full border-4 border-white shadow-xl object-cover"
                />
              ) : (
                <div className="w-32 h-32 rounded-full border-4 border-white shadow-xl bg-emerald-100 flex items-center justify-center">
                  <span className="text-4xl font-bold text-emerald-600">
                    {guide.firstName.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <div className="flex-1 text-white pb-2">
                <h1 className="text-3xl md:text-4xl font-bold mb-2">{guide.name}</h1>
                <div className="flex flex-wrap items-center gap-4 text-sm md:text-base">
                  {guide.avgRating > 0 && (
                    <>
                      <div className="flex items-center gap-1">
                        <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                        <span className="font-semibold">{guide.avgRating.toFixed(2)}</span>
                        <span>({guide.totalReviews} reviews)</span>
                      </div>
                      <span>&#8226;</span>
                    </>
                  )}
                  {guide.totalTrips > 0 && (
                    <>
                      <span>{guide.totalTrips} trips</span>
                      <span>&#8226;</span>
                    </>
                  )}
                  {guide.location && (
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      <span>{guide.location}</span>
                    </div>
                  )}
                </div>
              </div>
              {allServices.length > 0 && (
                <div className="hidden md:flex gap-2">
                  <button
                    onClick={scrollToTrips}
                    className={`${theme.buttonPrimary} px-6 py-3 rounded-lg font-semibold shadow-lg transition flex items-center gap-2`}
                  >
                    View Trips
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* About Guide Section */}
        <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
          <h2 className="text-xl font-bold mb-4">About {guide.firstName}</h2>
          {guide.bio ? (
            <p className="text-gray-700 leading-relaxed mb-4">{guide.bio}</p>
          ) : (
            <p className="text-gray-400 italic mb-4">This guide has not added a bio yet.</p>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            {guide.experienceRange && (
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className={`text-2xl font-bold ${theme.textPrimary}`}>{guide.experienceRange}</div>
                <div className="text-sm text-gray-600">Years Experience</div>
              </div>
            )}
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className={`text-2xl font-bold ${theme.textPrimary}`}>{guide.totalTrips}</div>
              <div className="text-sm text-gray-600">Trips Led</div>
            </div>
            {guide.avgRating > 0 && (
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className={`text-2xl font-bold ${theme.textPrimary}`}>{guide.avgRating.toFixed(1)}&#9733;</div>
                <div className="text-sm text-gray-600">Average Rating</div>
              </div>
            )}
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className={`text-2xl font-bold ${theme.textPrimary}`}>{guide.totalReviews}</div>
              <div className="text-sm text-gray-600">Reviews</div>
            </div>
          </div>

          {guide.certifications.length > 0 && (
            <div className="mt-6">
              <h3 className="font-semibold mb-3">Certifications & Verification</h3>
              <div className="flex flex-wrap gap-2 mb-3">
                {guide.certifications.map((cert, idx) => (
                  <span key={idx} className={`inline-flex items-center gap-1 px-3 py-1 ${theme.bgLight} ${theme.textPrimary} rounded-full text-sm`}>
                    <Award className="w-4 h-4" />
                    {cert}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Trust & Safety Badges */}
          <div className="flex flex-wrap gap-2 mt-3">
            {profile.email_verified && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                <Shield className="w-4 h-4" />
                Email Verified
              </span>
            )}
          </div>
        </div>

        {/* Services / Trips Section */}
        <div ref={tripsRef} className="mb-6">
          {allServices.length > 0 ? (
            <>
              <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                  <div>
                    <h2 className="text-xl font-bold mb-1">
                      Available Trips ({filteredServices.length})
                    </h2>
                    <p className="text-sm text-gray-600">Click any trip to view dates, pricing, and full details</p>
                  </div>
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className={`text-sm ${theme.textPrimary} ${theme.textHover} font-medium whitespace-nowrap`}
                    >
                      Clear all filters
                    </button>
                  )}
                </div>

                {/* Search & Filters */}
                <div className="border border-gray-300 rounded-xl p-4 bg-gray-50">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <select
                        value={selectedLocationFilter || 'All Locations'}
                        onChange={(e) => setSelectedLocationFilter(e.target.value === 'All Locations' ? null : e.target.value)}
                        className={`w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 ${theme.focusRing} ${theme.focusBorder} bg-white hover:border-gray-400 transition`}
                      >
                        {locations.map((location) => (
                          <option key={location} value={location}>{location}</option>
                        ))}
                      </select>
                    </div>

                    <div className="relative">
                      <Mountain className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <select
                        value={selectedActivityFilter || 'All Activities'}
                        onChange={(e) => setSelectedActivityFilter(e.target.value === 'All Activities' ? null : e.target.value)}
                        className={`w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 ${theme.focusRing} ${theme.focusBorder} bg-white hover:border-gray-400 transition`}
                      >
                        {activities.map((activity) => (
                          <option key={activity} value={activity}>{activity}</option>
                        ))}
                      </select>
                    </div>

                    <div className="relative">
                      <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <select
                        value={selectedSeasonFilter || 'All Seasons'}
                        onChange={(e) => setSelectedSeasonFilter(e.target.value === 'All Seasons' ? null : e.target.value)}
                        className={`w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 ${theme.focusRing} ${theme.focusBorder} bg-white hover:border-gray-400 transition`}
                      >
                        {seasons.map((season) => (
                          <option key={season} value={season}>{season}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Urgency Indicators */}
              {urgencyData.length > 0 && (
                <UrgencyIndicators indicators={urgencyData} variant="expanded" />
              )}

              {/* Service Cards Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-4 sm:gap-x-6 gap-y-8 sm:gap-y-10">
                {filteredServices.map((service) => (
                  <ExperienceCard key={service.id} experience={service} variant="guideProfile" />
                ))}
              </div>

              {filteredServices.length === 0 && (
                <div className="text-center py-12 bg-white rounded-xl">
                  <p className="text-gray-600">No trips match your selected filters.</p>
                  <button
                    onClick={clearFilters}
                    className={`mt-4 ${theme.textPrimary} ${theme.textHover} font-medium`}
                  >
                    Clear filters
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white rounded-xl p-8 shadow-sm text-center">
              <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h2 className="text-lg font-semibold text-gray-700 mb-2">No Experiences Listed Yet</h2>
              <p className="text-gray-500 text-sm">
                {guide.firstName} has not published any experiences yet. Check back soon!
              </p>
            </div>
          )}
        </div>

        {/* Reviews Section */}
        <ReviewsSection
          entityType="guide"
          entityId={guide.id}
          showAllLink={true}
        />
      </div>

      <Footer />
    </div>
  );
}
